import React, { useState } from 'react';
import type { AgentWebhookLogResponse, AgentWebhookLogStats, AIAgentWebhook } from '@troia-v3/types';

import { Modal, ModalContent, ModalHeader } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { useAgentWebhookLogs, useAgentWebhookLogStats } from '../hooks/useAgentWebhookLogs';

interface WebhookLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  webhook: AIAgentWebhook;
}

export const WebhookLogsModal: React.FC<WebhookLogsModalProps> = ({
  isOpen,
  onClose,
  webhook,
}) => {
  const [selectedLog, setSelectedLog] = useState<AgentWebhookLogResponse | null>(null);
  const [page, setPage] = useState(1);

  const { data: logsData, isLoading: logsLoading } = useAgentWebhookLogs(
    webhook.id,
    { page, limit: 15 },
    { enabled: isOpen }
  );
  const { data: statsData } = useAgentWebhookLogStats(webhook.id, { enabled: isOpen });

  const logs: AgentWebhookLogResponse[] = logsData?.data || [];
  const pagination = logsData?.pagination;
  const stats: AgentWebhookLogStats | undefined = statsData?.data;

  const handleClose = (): void => {
    setSelectedLog(null);
    setPage(1);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="4xl">
      <ModalHeader onClose={handleClose}>
        {selectedLog ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedLog(null)}
              className="p-1 rounded transition-colors"
              style={{ color: '#64748b' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              data-testid="webhook-logs-back-to-list"
            >
              <Icon name="arrow-left" className="w-4 h-4" />
            </button>
            <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
              Detalhes da Execução
            </h2>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg" style={{ backgroundColor: '#ede9fe' }}>
              <Icon name="activity" className="w-4 h-4" style={{ color: '#7c3aed' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                Log de Webhooks
              </h2>
              <p className="text-xs" style={{ color: '#64748b' }}>
                {webhook.name}
              </p>
            </div>
          </div>
        )}
      </ModalHeader>

      <ModalContent padding={false}>
        {selectedLog ? (
          <LogDetailView log={selectedLog} />
        ) : (
          <LogListView
            logs={logs}
            stats={stats}
            isLoading={logsLoading}
            pagination={pagination}
            page={page}
            onPageChange={setPage}
            onSelectLog={setSelectedLog}
          />
        )}
      </ModalContent>
    </Modal>
  );
};

WebhookLogsModal.displayName = 'WebhookLogsModal';

// ============================================================================
// Stats Bar
// ============================================================================

interface StatsBarProps {
  stats: AgentWebhookLogStats;
}

const StatsBar: React.FC<StatsBarProps> = ({ stats }) => {
  return (
    <div className="grid grid-cols-3 gap-3 p-4 border-b" style={{ borderColor: '#e5e7eb' }}>
      <div className="text-center">
        <p className="text-xs font-medium" style={{ color: '#64748b' }}>Total</p>
        <p className="text-lg font-bold" style={{ color: '#1e293b' }}>{stats.totalExecutions}</p>
      </div>
      <div className="text-center">
        <p className="text-xs font-medium" style={{ color: '#64748b' }}>Taxa de Sucesso</p>
        <p
          className="text-lg font-bold"
          style={{ color: stats.successRate >= 80 ? '#10b981' : stats.successRate >= 50 ? '#f59e0b' : '#ef4444' }}
        >
          {stats.successRate.toFixed(1)}%
        </p>
      </div>
      <div className="text-center">
        <p className="text-xs font-medium" style={{ color: '#64748b' }}>Tempo Médio</p>
        <p className="text-lg font-bold" style={{ color: '#1e293b' }}>{stats.avgExecutionTime.toFixed(0)}ms</p>
      </div>
    </div>
  );
};

// ============================================================================
// Log List View
// ============================================================================

interface LogListViewProps {
  logs: AgentWebhookLogResponse[];
  stats?: AgentWebhookLogStats;
  isLoading: boolean;
  pagination?: { page: number; totalPages: number; total: number };
  page: number;
  onPageChange: (page: number) => void;
  onSelectLog: (log: AgentWebhookLogResponse) => void;
}

const LogListView: React.FC<LogListViewProps> = ({
  logs,
  stats,
  isLoading,
  pagination,
  page,
  onPageChange,
  onSelectLog,
}) => {
  return (
    <div className="flex flex-col" style={{ maxHeight: '60vh' }} data-testid="webhook-logs-list">
      {stats && <StatsBar stats={stats} />}

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: '#7c3aed' }} />
          </div>
        )}

        {!isLoading && logs.length === 0 && (
          <div className="text-center py-12">
            <Icon name="activity" className="w-10 h-10 mx-auto mb-3" style={{ color: '#d1d5db' }} />
            <p className="text-sm font-medium" style={{ color: '#1e293b' }}>Nenhum log encontrado</p>
            <p className="text-xs mt-1" style={{ color: '#64748b' }}>As requisições recebidas por este webhook aparecerão aqui</p>
          </div>
        )}

        {!isLoading && logs.length > 0 && (
          <div>
            <div
              className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium"
              style={{ backgroundColor: '#f8fafc', color: '#64748b', borderBottom: '1px solid #e5e7eb' }}
            >
              <div className="col-span-3">Data/Hora</div>
              <div className="col-span-4">Requisição</div>
              <div className="col-span-2 text-center">Resultado</div>
              <div className="col-span-1 text-center">Tempo</div>
              <div className="col-span-2 text-center">Conversa</div>
            </div>

            {logs.map((log) => (
              <button
                key={log.id}
                onClick={() => onSelectLog(log)}
                className="w-full grid grid-cols-12 gap-2 px-4 py-2.5 text-left text-sm transition-colors"
                style={{ borderBottom: '1px solid #f1f5f9', color: '#1e293b' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                data-testid={`webhook-log-row-${log.id}`}
              >
                <div className="col-span-3 text-xs" style={{ color: '#64748b' }}>
                  {formatDate(log.createdAt)}
                </div>
                <div className="col-span-4 truncate text-xs" style={{ color: '#64748b' }}>
                  {truncateBody(log.request.body)}
                </div>
                <div className="col-span-2 flex items-center justify-center gap-1">
                  {log.filtered ? (
                    <>
                      <Icon name="filter" className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} />
                      <span className="text-xs" style={{ color: '#f59e0b' }}>Filtrado</span>
                    </>
                  ) : log.success ? (
                    <>
                      <Icon name="check-circle" className="w-3.5 h-3.5" style={{ color: '#10b981' }} />
                      <span className="text-xs" style={{ color: '#10b981' }}>Sucesso</span>
                    </>
                  ) : (
                    <>
                      <Icon name="x-circle" className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                      <span className="text-xs" style={{ color: '#ef4444' }}>Falha</span>
                    </>
                  )}
                </div>
                <div className="col-span-1 text-center">
                  <span className="text-xs flex items-center justify-center gap-0.5" style={{ color: '#64748b' }}>
                    <Icon name="clock" className="w-3 h-3" />
                    {log.executionTime}ms
                  </span>
                </div>
                <div className="col-span-2 text-center">
                  {log.conversationId ? (
                    <a
                      href={`/chat/${log.conversationId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs underline"
                      style={{ color: '#8b5cf6' }}
                      data-testid={`webhook-log-conversation-link-${log.id}`}
                    >
                      Abrir
                    </a>
                  ) : (
                    <span className="text-xs" style={{ color: '#d1d5db' }}>—</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: '#e5e7eb' }}>
          <p className="text-xs" style={{ color: '#64748b' }}>
            Página {pagination.page} de {pagination.totalPages} ({pagination.total} registros)
          </p>
          <div className="flex gap-2">
            {/* F54: mini-padrão de paginação de modal de logs — IGUAL ao CustomActionLogsModal */}
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
              className="px-3 py-1 text-xs rounded border transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb', color: '#1e293b' }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
              data-testid="webhook-logs-prev-page"
            >
              Anterior
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page === pagination.totalPages}
              className="px-3 py-1 text-xs rounded border transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb', color: '#1e293b' }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
              data-testid="webhook-logs-next-page"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Log Detail View
// ============================================================================

interface LogDetailViewProps {
  log: AgentWebhookLogResponse;
}

const LogDetailView: React.FC<LogDetailViewProps> = ({ log }) => {
  return (
    <div className="overflow-y-auto" style={{ maxHeight: '65vh' }} data-testid="webhook-log-detail">
      <div className="grid grid-cols-4 gap-3 p-4 border-b" style={{ borderColor: '#e5e7eb' }}>
        <div>
          <p className="text-xs font-medium" style={{ color: '#64748b' }}>Status</p>
          <div className="flex items-center gap-1 mt-1">
            {log.filtered ? (
              <>
                <Icon name="filter" className="w-4 h-4" style={{ color: '#f59e0b' }} />
                <span className="text-sm font-medium" style={{ color: '#f59e0b' }}>Filtrado</span>
              </>
            ) : log.success ? (
              <>
                <Icon name="check-circle" className="w-4 h-4" style={{ color: '#10b981' }} />
                <span className="text-sm font-medium" style={{ color: '#10b981' }}>Sucesso</span>
              </>
            ) : (
              <>
                <Icon name="x-circle" className="w-4 h-4" style={{ color: '#ef4444' }} />
                <span className="text-sm font-medium" style={{ color: '#ef4444' }}>Falha</span>
              </>
            )}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#64748b' }}>Tempo</p>
          <p className="text-sm font-medium mt-1" style={{ color: '#1e293b' }}>{log.executionTime}ms</p>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#64748b' }}>Conversa</p>
          {log.conversationId ? (
            <a
              href={`/chat/${log.conversationId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm underline mt-1 inline-block"
              style={{ color: '#8b5cf6' }}
              data-testid="webhook-log-detail-conversation-link"
            >
              Abrir conversa
            </a>
          ) : (
            <p className="text-sm mt-1" style={{ color: '#94a3b8' }}>—</p>
          )}
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: '#64748b' }}>Data</p>
          <p className="text-sm mt-1" style={{ color: '#1e293b' }}>{formatDate(log.createdAt)}</p>
        </div>
      </div>

      {log.error && (
        <div className="mx-4 mt-4 p-3 rounded-lg" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
          <p className="text-xs font-medium" style={{ color: '#991b1b' }}>Erro</p>
          <p className="text-sm mt-1" style={{ color: '#b91c1c' }}>{log.error}</p>
        </div>
      )}

      <div className="p-4 space-y-3">
        <CollapsibleSection title="Request" defaultOpen>
          <div className="space-y-3">
            {log.request.headers && Object.keys(log.request.headers).length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Headers</p>
                <JsonBlock data={log.request.headers} />
              </div>
            )}
            {log.request.body !== undefined && log.request.body !== null && (
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Body</p>
                <JsonBlock data={log.request.body} />
              </div>
            )}
          </div>
        </CollapsibleSection>

        {log.aiProcessing && (
          <CollapsibleSection title="Processamento de IA" defaultOpen>
            <div className="space-y-3">
              {log.aiProcessing.prompt && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Prompt</p>
                  <pre
                    className="text-xs p-3 rounded overflow-x-auto whitespace-pre-wrap"
                    style={{ backgroundColor: '#f8fafc', color: '#1e293b', border: '1px solid #e5e7eb' }}
                  >
                    {log.aiProcessing.prompt}
                  </pre>
                </div>
              )}
              {log.aiProcessing.result !== undefined && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: '#64748b' }}>Resultado</p>
                  <JsonBlock data={log.aiProcessing.result} />
                </div>
              )}
              {log.aiProcessing.error && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: '#991b1b' }}>Erro</p>
                  <pre
                    className="text-xs p-3 rounded overflow-x-auto whitespace-pre-wrap"
                    style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
                  >
                    {log.aiProcessing.error}
                  </pre>
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        <CollapsibleSection title="Execução do Agente">
          {log.agentExecution ? (
            <div className="flex items-center gap-2">
              {log.agentExecution.success ? (
                <>
                  <Icon name="check-circle" className="w-4 h-4" style={{ color: '#10b981' }} />
                  <span className="text-sm" style={{ color: '#10b981' }}>Agente executado com sucesso</span>
                </>
              ) : (
                <>
                  <Icon name="x-circle" className="w-4 h-4" style={{ color: '#ef4444' }} />
                  <span className="text-sm" style={{ color: '#ef4444' }}>{log.agentExecution.error || 'Falha na execução do agente'}</span>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Icon name="clock" className="w-4 h-4" style={{ color: '#94a3b8' }} />
              <span className="text-sm" style={{ color: '#94a3b8' }}>Pendente ou não aplicável</span>
            </div>
          )}
        </CollapsibleSection>
      </div>
    </div>
  );
};

// ============================================================================
// Collapsible Section
// ============================================================================

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg" style={{ border: '1px solid #e5e7eb' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium transition-colors"
        style={{ color: '#1e293b', backgroundColor: '#f8fafc', borderRadius: isOpen ? '0.5rem 0.5rem 0 0' : '0.5rem' }}
        data-testid={`webhook-log-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {title}
        {isOpen ? (
          <Icon name="chevron-down" className="w-4 h-4" style={{ color: '#64748b' }} />
        ) : (
          <Icon name="chevron-right" className="w-4 h-4" style={{ color: '#64748b' }} />
        )}
      </button>
      {isOpen && (
        <div className="px-3 py-3" style={{ borderTop: '1px solid #e5e7eb' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// JSON Block
// ============================================================================

const JsonBlock: React.FC<{ data: unknown }> = ({ data }) => {
  const formatted = typeof data === 'string' ? data : JSON.stringify(data, null, 2);

  return (
    <pre
      className="text-xs p-3 rounded overflow-x-auto font-mono"
      style={{
        backgroundColor: '#f8fafc',
        color: '#1e293b',
        border: '1px solid #e5e7eb',
        maxHeight: '300px',
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {formatted}
    </pre>
  );
};

// ============================================================================
// Helpers
// ============================================================================

function formatDate(dateStr: Date | string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function truncateBody(body: unknown): string {
  if (body === undefined || body === null) return '—';
  const str = typeof body === 'string' ? body : JSON.stringify(body);
  return str.length > 60 ? `${str.slice(0, 57)}...` : str;
}
